/**
 * Lane Arranger
 * Handles rearranging lanes to stack vertically within pools.
 * Uses simple sequential stacking for lane positioning.
 */

import type { ElkNode, ElkExtendedEdge } from 'elkjs';
import type { ElkBpmnGraph } from '../../types';
import type { NodeWithBpmn, Point } from '../../types/internal';
import { isDebugEnabled } from '../../utils/debug';

type ElkNodeWithBpmn = ElkNode & { bpmn?: NodeWithBpmn['bpmn'] };

/**
 * Handler for lane rearrangement
 */
export class LaneArranger {
  private readonly laneHeaderWidth = 30;
  private readonly laneMinHorizontalPadding = 32;
  private readonly laneMaxHorizontalPadding = 96;
  private readonly laneMinVerticalPadding = 24;
  private readonly laneMaxVerticalPadding = 80;
  private readonly emptyLaneContentWidth = 220;
  // Empty lanes should not claim node-sized vertical real estate.
  private readonly emptyLaneMinHeight = 60;

  /**
   * Rearrange lanes within pools to stack vertically
   */
  rearrange(layouted: ElkNode, original: ElkBpmnGraph): void {
    // Find pools in collaborations and process them
    if (layouted.children) {
      for (let i = 0; i < layouted.children.length; i++) {
        const child = layouted.children[i];
        const origChild = original.children?.[i] as NodeWithBpmn | undefined;

        // Check if this is a collaboration with pools
        if (origChild?.bpmn?.type === 'collaboration' && child.children) {
          for (let j = 0; j < child.children.length; j++) {
            const pool = child.children[j];
            const origPool = (origChild.children as NodeWithBpmn[] | undefined)?.[j];
            if (origPool?.bpmn?.type === 'participant') {
              this.processPool(pool, origPool);
            }
          }
        } else if (origChild?.bpmn?.type === 'participant') {
          // Direct pool (not in collaboration)
          this.processPool(child, origChild);
        }
      }
    }
  }

  /**
   * Process a single pool to arrange its lanes
   */
  private processPool(pool: ElkNode, origPool: NodeWithBpmn | undefined): void {
    if (!pool.children || !origPool?.children) return;

    // Check if this pool has lanes
    const hasLanes = (origPool.children as NodeWithBpmn[]).some(c => c.bpmn?.type === 'lane');
    if (!hasLanes) return;

    // Get layouted nodes (flattened at pool level after ELK)
    const layoutedNodes = new Map<string, ElkNode>();
    let maxNodeWidth = 100;
    for (const child of pool.children) {
      layoutedNodes.set(child.id, child);
      maxNodeWidth = Math.max(maxNodeWidth, child.width ?? 100);
    }

    const horizontalPadding = this.calculateHorizontalPadding(maxNodeWidth);

    // Compute pool-wide global X span so all lanes share the same horizontal
    // coordinate system. Without this, each lane recenters to its own minX
    // and ELK's column alignment across lanes is destroyed.
    let globalMinX = Infinity;
    let globalMaxX = 0;
    for (const child of pool.children) {
      const x = child.x ?? 0;
      const w = child.width ?? 100;
      if (x < globalMinX) globalMinX = x;
      if (x + w > globalMaxX) globalMaxX = x + w;
    }
    const contentSpan = isFinite(globalMinX) ? globalMaxX - globalMinX : 0;
    // Top-level lane content origin equals horizontalPadding (lane-relative).
    // Each leaf node's final lane-relative x = (origPoolX - globalMinX) + horizontalPadding.
    const globalXOffset = isFinite(globalMinX) ? (horizontalPadding - globalMinX) : 0;

    // Calculate pool width (content width + dynamic side paddings)
    const poolContentWidth = Math.max(
      this.emptyLaneContentWidth + horizontalPadding * 2,
      contentSpan + horizontalPadding * 2
    );

    // Build lane structure - lanes fill the full pool width
    const result = this.buildLaneStructure(
      origPool.children as NodeWithBpmn[],
      layoutedNodes,
      0,
      poolContentWidth,
      horizontalPadding,
      globalXOffset
    );

    // Update pool - lanes fill the entire pool, no gaps
    pool.children = result.lanes;
    pool.width = this.laneHeaderWidth + poolContentWidth;
    pool.height = result.totalHeight;

    // Recalculate edge waypoints
    if (pool.edges) {
      this.recalculatePoolEdges(pool, result.lanes);
    }
  }

  /**
   * Recursively build lane structure with positioned nodes
   * Uses ConstraintSolver for vertical stacking
   */
  private buildLaneStructure(
    origChildren: NodeWithBpmn[],
    layoutedNodes: Map<string, ElkNode>,
    startY: number,
    maxRight: number,
    horizontalPadding: number,
    xOffset: number
  ): { lanes: ElkNode[]; totalHeight: number } {
    const lanes: ElkNode[] = [];

    // Filter to get only lanes and sort by partition
    const origLanes = origChildren.filter(c => c.bpmn?.type === 'lane');
    origLanes.sort((a, b) => {
      const partA = a.layoutOptions?.['elk.partitioning.partition'];
      const partB = b.layoutOptions?.['elk.partitioning.partition'];
      return (partA !== undefined ? Number(partA) : 0) - (partB !== undefined ? Number(partB) : 0);
    });

    if (origLanes.length === 0) {
      return { lanes: [], totalHeight: 0 };
    }

    // Calculate heights for each lane
    const laneHeights = new Map<string, number>();
    const laneNodes = new Map<string, ElkNode[]>();

    for (const origLane of origLanes) {
      const hasNestedLanes = origLane.children?.some((c: unknown) => (c as NodeWithBpmn).bpmn?.type === 'lane');

      if (hasNestedLanes) {
        const nestedWidth = maxRight - this.laneHeaderWidth;
        // Nested lanes sit at x=laneHeaderWidth within their parent lane, so
        // their inner X system is shifted by another laneHeaderWidth.
        const nested = this.buildLaneStructure(
          origLane.children as NodeWithBpmn[],
          layoutedNodes,
          0,
          nestedWidth,
          horizontalPadding,
          xOffset - this.laneHeaderWidth
        );
        laneHeights.set(origLane.id, nested.totalHeight);
        laneNodes.set(origLane.id, nested.lanes);
      } else {
        const nodesInLane: ElkNode[] = [];
        if (origLane.children) {
          for (const child of origLane.children) {
            const node = layoutedNodes.get((child as NodeWithBpmn).id);
            if (node) nodesInLane.push(node);
          }
        }

        let minY = Infinity;
        let maxY = 0;
        let maxNodeHeight = 80;
        for (const node of nodesInLane) {
          minY = Math.min(minY, node.y ?? 0);
          maxY = Math.max(maxY, (node.y ?? 0) + (node.height ?? 80));
          maxNodeHeight = Math.max(maxNodeHeight, node.height ?? 80);
        }

        let laneHeight: number;
        let yOffset = 0;
        if (nodesInLane.length > 0) {
          const contentHeight = maxY - minY;
          const verticalPadding = this.calculateVerticalPadding(maxNodeHeight);
          laneHeight = contentHeight + verticalPadding * 2;
          yOffset = verticalPadding - minY;
        } else {
          // Empty lane: collapse to a slim baseline rather than reserving
          // node-sized vertical space.
          laneHeight = this.emptyLaneMinHeight;
        }

        // Per-lane Y offset (each lane has its own vertical baseline) but
        // shared X offset (preserves ELK's column alignment across lanes).
        for (const node of nodesInLane) {
          node.x = (node.x ?? 0) + xOffset;
          node.y = (node.y ?? 0) + yOffset;
        }

        laneHeights.set(origLane.id, laneHeight);
        laneNodes.set(origLane.id, nodesInLane);
      }
    }

    // Simple sequential stacking - lanes stack from startY downward
    let currentY = startY;

    // Build lane nodes with sequential positions
    for (const origLane of origLanes) {
      const height = laneHeights.get(origLane.id) ?? 100;
      const children = laneNodes.get(origLane.id) ?? [];
      const hasNestedLanes = origLane.children?.some((c: unknown) => (c as NodeWithBpmn).bpmn?.type === 'lane');

      if (hasNestedLanes) {
        for (const nestedLane of children) {
          nestedLane.width = maxRight - this.laneHeaderWidth;
        }
      }

      const laneNode: ElkNodeWithBpmn = {
        id: origLane.id,
        x: this.laneHeaderWidth,
        y: currentY,
        width: maxRight,
        height: height,
        children: children,
        bpmn: origLane.bpmn,
      };
      lanes.push(laneNode);
      currentY += height;
    }

    const totalHeight = currentY - startY;
    return { lanes, totalHeight };
  }

  private calculateHorizontalPadding(maxNodeWidth: number): number {
    return this.clamp(Math.round(maxNodeWidth * 0.45), this.laneMinHorizontalPadding, this.laneMaxHorizontalPadding);
  }

  private calculateVerticalPadding(maxNodeHeight: number): number {
    return this.clamp(Math.round(maxNodeHeight * 0.5), this.laneMinVerticalPadding, this.laneMaxVerticalPadding);
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }


  /**
   * Recalculate edge waypoints after node positions have shifted (lane stacking).
   * Produces a simple perpendicular L-shape. Obstacle avoidance is delegated to EdgeFixer.
   */
  private recalculatePoolEdges(pool: ElkNode, lanes: ElkNode[]): void {
    if (!pool.edges) return;

    const nodePositions = this.collectAbsoluteNodePositions(lanes);

    for (const edge of pool.edges) {
      const sourceId = edge.sources?.[0];
      const targetId = edge.targets?.[0];
      const src = sourceId ? nodePositions.get(sourceId) : undefined;
      const dst = targetId ? nodePositions.get(targetId) : undefined;
      if (!src || !dst) continue;

      const waypoints = this.buildSimpleLRoute(src, dst);
      (edge as ElkExtendedEdge & { _poolRelativeCoords?: boolean })._poolRelativeCoords = true;
      const last = waypoints[waypoints.length - 1];
      const first = waypoints[0];
      if (!first || !last) continue;
      edge.sections = [{
        id: `${edge.id}_s0`,
        startPoint: first,
        endPoint: last,
        bendPoints: waypoints.length > 2 ? waypoints.slice(1, -1) : undefined,
      }];
    }
  }

  /**
   * Collect absolute (pool-relative) positions of all flow nodes inside the given lanes,
   * recursing through nested lanes.
   */
  private collectAbsoluteNodePositions(
    lanes: ElkNode[]
  ): Map<string, { x: number; y: number; width: number; height: number }> {
    const positions = new Map<string, { x: number; y: number; width: number; height: number }>();
    const walk = (container: ElkNode, offsetX: number, offsetY: number) => {
      if (!container.children) return;
      for (const child of container.children) {
        const absX = offsetX + (child.x ?? 0);
        const absY = offsetY + (child.y ?? 0);
        positions.set(child.id, {
          x: absX,
          y: absY,
          width: child.width ?? 100,
          height: child.height ?? 80,
        });
        walk(child, absX, absY);
      }
    };
    for (const lane of lanes) {
      walk(lane, lane.x ?? 0, lane.y ?? 0);
    }
    return positions;
  }

  /**
   * Build a perpendicular L-shape route between two nodes. The shape prefers
   * horizontal flow (right→left) when source is left of target, falling back
   * to vertical (bottom→top) when nodes are nearly column-aligned.
   * EdgeFixer will replace this with a pathfinding route if it crosses nodes.
   */
  private buildSimpleLRoute(
    src: { x: number; y: number; width: number; height: number },
    dst: { x: number; y: number; width: number; height: number }
  ): Point[] {
    const srcCx = src.x + src.width / 2;
    const srcCy = src.y + src.height / 2;
    const dstCx = dst.x + dst.width / 2;
    const dstCy = dst.y + dst.height / 2;
    const dx = dstCx - srcCx;
    const dy = dstCy - srcCy;

    // Choose horizontal-primary unless nodes are clearly stacked vertically.
    const horizontalPrimary = Math.abs(dx) > 30 || Math.abs(dy) < 50;

    let startX: number, startY: number, endX: number, endY: number;
    if (horizontalPrimary) {
      startY = srcCy;
      endY = dstCy;
      if (dx >= 0) {
        startX = src.x + src.width;
        endX = dst.x;
      } else {
        startX = src.x;
        endX = dst.x + dst.width;
      }
    } else {
      startX = srcCx;
      endX = dstCx;
      if (dy >= 0) {
        startY = src.y + src.height;
        endY = dst.y;
      } else {
        startY = src.y;
        endY = dst.y + dst.height;
      }
    }

    const points: Point[] = [{ x: startX, y: startY }];
    if (horizontalPrimary && Math.abs(startY - endY) > 1) {
      // Bend at midpoint of horizontal span
      const midX = (startX + endX) / 2;
      points.push({ x: midX, y: startY });
      points.push({ x: midX, y: endY });
    } else if (!horizontalPrimary && Math.abs(startX - endX) > 1) {
      const midY = (startY + endY) / 2;
      points.push({ x: startX, y: midY });
      points.push({ x: endX, y: midY });
    }
    points.push({ x: endX, y: endY });
    return points;
  }
}
